import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { copyContent } from "./copyAction";
import "./markdown-renderer.css";

const markdownSyntaxRegex = /([*_~`]|^#{1,6}\s|^\[([^\]]+)\]\([^)]+\))/gm;

const cleanMarkdownText = (markdown) => markdown.replace(markdownSyntaxRegex, "");

const handleBlockCopy = (e) => {
   e.stopPropagation();
   const el = e.currentTarget;
   let text = el?.innerText?.trim() || "";
   if (el.tagName === "LI" && text) text = `- ${text}`;
   if (text) copyContent(text);
};

const handleSectionCopy = (e) => {
   e.stopPropagation();
   const sectionEl = e.currentTarget.closest(".medical-report-section");
   const markdown = sectionEl?.dataset?.markdown;
   if (markdown) {
      copyContent(cleanMarkdownText(markdown));
   } else {
      const text = sectionEl?.innerText?.trim() || "";
      if (text) copyContent(text);
   }
};

const handleHoverStart = (e) => {
   e.currentTarget?.classList.add("copyable-hovered");
};

const handleHoverEnd = (e) => {
   e.currentTarget?.classList.remove("copyable-hovered");
};

const handleSectionHoverStart = (e) => {
   e.currentTarget?.closest(".medical-report-section")?.classList.add("copyable-hovered");
};

const handleSectionHoverEnd = (e) => {
   e.currentTarget?.closest(".medical-report-section")?.classList.remove("copyable-hovered");
};

const handleCopyKeyDown = (e, copyHandler) => {
   if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      copyHandler(e);
   }
};

const splitMarkdownIntoSections = (markdown) => {
   const lines = markdown.split("\n");
   const sections = [];
   let currentSection = [];
   const headingRegex = /^#{1,6}\s/;

   for (const line of lines) {
      if (headingRegex.test(line.trim())) {
         if (currentSection.length > 0) {
            sections.push(currentSection.join("\n"));
            currentSection = [];
         }
      }
      currentSection.push(line);
   }
   if (currentSection.length > 0) {
      sections.push(currentSection.join("\n"));
   }
   return sections;
};

const MedicalReport = ({ content, SummaryData }) => {
   const [fade, setFade] = useState(false);

   useEffect(() => {
      if (SummaryData?.status === "updating") {
         const timer = setTimeout(() => {
            setFade(true);
         }, 1000);

         return () => clearTimeout(timer);
      } else {
         setFade(false);
      }
   }, [SummaryData?.status]);

   const renderers = useMemo(
      () => ({
         heading: (props) => {
            const Tag = `h${props.level}`;
            return (
               <div
                  onClick={handleSectionCopy}
                  onKeyDown={(e) => handleCopyKeyDown(e, handleSectionCopy)}
                  onMouseEnter={handleSectionHoverStart}
                  onMouseLeave={handleSectionHoverEnd}
                  className="medical-report-copyable-section"
                  role="button"
                  tabIndex={0}
                  aria-label="Copy section"
               >
                  <Tag>{props.children}</Tag>
               </div>
            );
         },
         paragraph: (props) => (
            <div
               onClick={handleBlockCopy}
               onKeyDown={(e) => handleCopyKeyDown(e, handleBlockCopy)}
               onMouseEnter={handleHoverStart}
               onMouseLeave={handleHoverEnd}
               className="medical-report-copyable-section"
               role="button"
               tabIndex={0}
               aria-label="Copy paragraph"
            >
               <p>{props.children}</p>
            </div>
         ),
         listItem: (props) => (
            <li
               {...props}
               onClick={handleBlockCopy}
               onKeyDown={(e) => handleCopyKeyDown(e, handleBlockCopy)}
               onMouseEnter={handleHoverStart}
               onMouseLeave={handleHoverEnd}
               className="medical-report-copyable-section"
               role="button"
               tabIndex={0}
               aria-label="Copy list item"
            />
         ),
      }),
      []
   );

   if (!content) return null;

   if (!content?.includes("###")) {
      return (
         <div className={`${fade ? "fade-out fade-out-opacity" : ""}`} style={{ fontSize: "17px", paddingRight: "20px" }}>
            <pre
               style={{
                  whiteSpace: "pre-wrap",
                  fontFamily: "inherit",
                  fontSize: "17px",
               }}
            >
               {content}
            </pre>
         </div>
      );
   }

   const sections = splitMarkdownIntoSections(content);

   return (
      <div className={`${fade ? "fade-out fade-out-opacity" : ""}`} style={{ fontSize: "17px", paddingRight: "20px" }}>
         <div className="medical-report-markdown">
            {sections.map((sectionMarkdown, i) => (
               <div key={i} className="medical-report-section" data-markdown={sectionMarkdown}>
                  <ReactMarkdown source={sectionMarkdown} renderers={renderers} />
               </div>
            ))}
         </div>
      </div>
   );
};

export default MedicalReport;
